/**
 * The dispatch step's study picker (SA-859, route studies spec D9): the ids it
 * binds to, its contract check, the one WRITER of a study pin and the ranking.
 * Pure: no React, no fetch — the manifest imports `matchesStudyPicker` eagerly.
 *
 * `RS-<n> v<m>` has one reader, the server's `studies/reference.py::parse_reference`.
 * This side never reads it (design note D5): a row is "chosen" when the text it
 * would write equals the field's text. `shared/study-reference-cases.json` pins
 * `pinText` against the reader.
 */
import { stationCode } from '@/components/records/stationCode'
import type { TaskRead } from '@/types/process'
import { carries } from '../contract'
import { fromRows, liveRuns, readRows } from '../sectors/sectorModel'
import { versionPath } from './address'
import type { CostSheetPrefillRead, StudyListRow } from './types'

export const STEP_DISPATCH = 'dispatch_request'
export const EXIT_REUSED = 'reused'
export const EXIT_NEW = 'new'
export const F_REFERENCE = 'study_reference'
export const F_LINK = 'study_link'
export const F_AIRAC = 'airac_validity'
export const NEED_SECTORS = 'sectors'

/** The view can do its job: both fields on the form as strings and both exits
 * offered, plus `carries`' rules (nothing redacted, no AI proposal). */
export function matchesStudyPicker(task: TaskRead): boolean {
  if (!carries(task, [F_REFERENCE, F_LINK], [EXIT_REUSED, EXIT_NEW])) return false
  return [F_REFERENCE, F_LINK].every((id) => task.form.find((f) => f.id === id)?.type === 'string')
}

/** `("RS-142", 7)` → `"RS-142 v7"`: a study's reference and one of its versions. */
export function pinText(reference: string, version: number): string {
  return `${reference} v${version}`
}

/** The row whose written pin the text is, ignoring case and surrounding
 * spaces (SA-878) — `parse_reference` ignores case too. Still writing and
 * comparing, never reading (D5). Words around a pin match nothing. */
export function pinnedRow(rows: StudyListRow[], text: unknown): StudyListRow | null {
  if (typeof text !== 'string') return null
  const typed = text.trim().toUpperCase()
  if (!typed) return null
  return rows.find((row) => pinText(row.reference, row.version_number).toUpperCase() === typed) ?? null
}

/** That version's page, absolute (design note D1): the pricing card links only an http(s) URL. */
export function pinHref(origin: string, reference: string, version: number): string {
  return `${origin}${versionPath(reference, version)}`
}

/** A link into this app's route-studies pages, whoever wrote it. It names one
 * study version, so it cannot stay beside a reference typed over the one it
 * came with — even when the pair arrived pre-filled from an earlier round or a
 * saved draft (SA-859 review #1). A link elsewhere is the person's own. */
export function isStudyLink(link: unknown, origin: string): boolean {
  return typeof link === 'string' && link.startsWith(`${origin}/route-studies?`)
}

/** The record's route for the ranking (SA-1037 §5.1): the whole rotation and
 * the live stops, each in order, each stop through the one text-to-code rule
 * (SA-1053: "Hong Kong International (HKG)" is HKG), else upper-cased. A stop
 * repeated by its neighbor is kept once. Empty without sectors. */
export interface RouteStops { rotation: string[]; live: string[] }

const code = (stop: string): string => stationCode(stop) ?? stop.toUpperCase()
const collapse = (stops: string[]): string[] => stops.filter((s, i) => i === 0 || s !== stops[i - 1])

export function routeStops(sectors: unknown): RouteStops {
  const r = fromRows(readRows(sectors), null)
  return { rotation: collapse(r.stops.map(code)), live: collapse(liveRuns(r).flat().map(code)) }
}

/** The live stops, each once, in route order: the M of "shares N of M stops". */
export function distinctLive(stops: RouteStops): string[] {
  return [...new Set(stops.live)]
}

export type StudyTier = 1 | 2 | 3 | 4 | 5
/** `matches` is tiers 1–3, the rotations dialog's mark (SA-839). */
export interface RankedStudy { row: StudyListRow; tier: StudyTier; shared: number; of: number; matches: boolean }

/** `stops` in order inside `route`, gaps allowed (a technical stop). */
function holdsInOrder(route: string[], stops: string[]): boolean {
  let at = 0
  for (const s of route) if (at < stops.length && s === stops[at]) at += 1
  return at === stops.length
}

/** Each study's tier, best first, each tier in the list read's own order (SA-1037 §5.1):
 * 1 the study's route holds the whole rotation in order; 2 it holds the live stops in
 * order; 3 every live stop in some order; 4 some live stop; 5 none, or no live stops. */
export function rankStudies(rows: StudyListRow[], stops: RouteStops): RankedStudy[] {
  const live = distinctLive(stops)
  const ranked = rows.map((row): RankedStudy => {
    const route = row.route.flatMap((station) => (station ? [station.toUpperCase()] : []))
    const held = new Set(route)
    const shared = live.filter((s) => held.has(s)).length
    const tier: StudyTier = live.length === 0 ? 5
      : stops.rotation.length >= 2 && holdsInOrder(route, stops.rotation) ? 1
      : holdsInOrder(route, stops.live) ? 2
      : shared === live.length ? 3
      : shared > 0 ? 4
      : 5
    return { row, tier, shared, of: live.length, matches: tier <= 3 }
  })
  return ([1, 2, 3, 4, 5] as const).flatMap((t) => ranked.filter((r) => r.tier === t))
}

/** One sector of the record's rotation as a leg (SA-1322 §4.1): its two stations through the same
 * text-to-code rule `routeStops` uses, and whether it is flown live. */
export interface RouteLeg { from: string; to: string; live: boolean }

export function routeLegs(sectors: unknown): RouteLeg[] {
  const r = fromRows(readRows(sectors), null)
  return r.sectors.map((s, i) => ({ from: code(r.stops[i]), to: code(r.stops[i + 1]), live: s.flownAs === 'live' }))
}

export type Coverage = 'all' | 'live' | 'none'
export interface CoveredStudy { row: StudyListRow; coverage: Coverage; held: RouteLeg[]; unheld: RouteLeg[]; ranked: RankedStudy }

/** The consecutive pairs of a study's route, upper-cased; a null stop breaks the chain. */
function routePairs(route: (string | null)[]): Set<string> {
  const pairs = new Set<string>()
  for (let i = 0; i + 1 < route.length; i += 1) {
    const a = route[i]
    const b = route[i + 1]
    if (a && b) pairs.add(`${a.toUpperCase()}\u0000${b.toUpperCase()}`)
  }
  return pairs
}
const holds = (pairs: Set<string>, leg: RouteLeg): boolean => pairs.has(`${leg.from}\u0000${leg.to}`)

/** Every study, by what it covers of the rotation's legs (SA-1322 §4.1): 'all' holds every leg as a
 * consecutive pair of its route, 'live' every live leg, 'none' the rest. 'all' then 'live' keep the list
 * read's order; 'none' keeps `rankStudies`' order and tiers. A rotation with no live leg covers nothing. */
export function coverStudies(rows: StudyListRow[], legs: RouteLeg[], stops: RouteStops): CoveredStudy[] {
  const ranked = rankStudies(rows, stops)
  const tierOf = new Map(ranked.map((r) => [r.row, r]))
  const hasLive = legs.some((l) => l.live)
  const covered = rows.map((row): CoveredStudy => {
    const pairs = routePairs(row.route)
    const held = legs.filter((l) => holds(pairs, l))
    const unheld = legs.filter((l) => !holds(pairs, l))
    const coverage: Coverage = !hasLive ? 'none'
      : unheld.length === 0 ? 'all'
      : unheld.every((l) => !l.live) ? 'live'
      : 'none'
    return { row, coverage, held, unheld, ranked: tierOf.get(row)! }
  })
  const of = (c: Coverage) => covered.filter((x) => x.coverage === c)
  const none = of('none')
  const order = new Map(ranked.map((r, i) => [r.row, i]))
  none.sort((a, b) => order.get(a.row)! - order.get(b.row)!)
  return [...of('all'), ...of('live'), ...none]
}

/** The live legs no study holds, in route order. */
export function uncoveredLive(rows: StudyListRow[], legs: RouteLeg[]): RouteLeg[] {
  const all = rows.map((r) => routePairs(r.route))
  return legs.filter((l) => l.live && !all.some((p) => holds(p, l)))
}

/** A words string without the key it opens with (`HKG · B744 …` under the row `HKG`). */
export function withoutKeyPrefix(words: string | null, key: string): string | null {
  if (words === null) return null
  return words.startsWith(`${key} · `) ? words.slice(key.length + 3) : words
}

/** A words string without the source word that repeats the candidate's kind, the first such segment only. */
export function withoutSourceWord(words: string | null, kind: string | null): string | null {
  if (words === null || (kind !== 'study' && kind !== 'typed')) return words
  const parts = words.split(' · ')
  const at = parts.indexOf(kind)
  if (at < 0) return words
  parts.splice(at, 1)
  return parts.join(' · ')
}

/** What "assemble from reference data" would build, read off the step-8 pre-fill's no-pin answer (SA-1211 D1, D2). */
export interface AssembledOffer {
  offered: boolean
  legs: { key: string; from: string | null; to: string | null; chosen: string | null; words: string | null; estimated: boolean }[]
  legCount: number
  stops: { station: string; found: boolean; words: string | null }[]
  estimated: number
  missing: { from: string | null; to: string | null }[]
}

/** Offered when every leg has a candidate (an estimate counts, with a mark); a stop with no charge set is listed and
 * never blocks. Null without an answer, off the engine, or with no legs. */
export function assembledOffer(answer: CostSheetPrefillRead | null | undefined): AssembledOffer | null {
  if (!answer || answer.engine !== true || answer.legs.length === 0) return null
  const legs = answer.legs.map((leg) => {
    const chosen = leg.reference?.chosen ?? null
    return {
      key: `${leg.from ?? '—'}-${leg.to ?? '—'}`, from: leg.from, to: leg.to, chosen,
      words: chosen === null ? null : leg.reference?.candidates.find((c) => c.kind === chosen)?.words ?? null,
      estimated: chosen === 'estimate',
    }
  })
  const missing = legs.filter((l) => l.chosen === null).map((l) => ({ from: l.from, to: l.to }))
  return {
    offered: missing.length === 0,
    legs,
    legCount: answer.legs.length,
    stops: (answer.visits ?? []).map((v) => ({ station: v.station, found: v.charges?.found ?? false, words: v.charges?.words ?? null })),
    estimated: answer.coverage?.estimated ?? legs.filter((l) => l.estimated).length,
    missing,
  }
}
